import {
  createCourse as apiCreateCourse,
  deleteCourse as apiDeleteCourse,
  processScormPackage as apiProcessScormPackage,
  updateCourse as apiUpdateCourse,
  assignSurveyToCourse,
  type CourseDetailResponse,
  getCourseById,
  type ProcessScormResponse,
  removeSurveyAssignment,
  setCourseLocale,
} from '@lms/api-client/backend';
import type { Locale } from '@/features/locale/supported-languages';

// Ports app/[brand]/admin/courses/_components/actions.ts. The Next server
// actions used revalidatePath / redirect for cache + navigation; here these are
// plain async functions and the callers revalidate via SWR `mutate` and
// navigate with `useNavigate`.

export async function saveCourseImage(
  image: string,
  courseId: number,
): Promise<void> {
  await apiUpdateCourse(courseId, { image });
}

/**
 * Persist location of SCORM package in S3.
 */
export async function saveScormPackage(
  url: string,
  filename: string,
  language: Locale,
  courseId: number,
): Promise<void> {
  // Fetch current course to get existing locale data.
  const current = await getCourseById(courseId);
  const existingLocale = current.data?.[language];

  // Merge existing data with new data.
  await setCourseLocale(courseId, language, {
    name: existingLocale?.name ?? '_pending_',
    scorm: existingLocale?.scorm ?? '_pending_',
    zip: url,
    filename,
  });
}

/**
 * Process a SCORM package: trigger reload, parse manifest, update course.
 * All the heavy lifting is done in the Rust backend.
 */
export async function processScormPackage(
  url: string,
  language: Locale,
  courseId: number,
): Promise<ProcessScormResponse> {
  return apiProcessScormPackage(courseId, url, language);
}

export async function removeCourse(courseId: number): Promise<void> {
  await apiDeleteCourse(courseId);
}

export async function createCourse(
  locationId: number,
): Promise<CourseDetailResponse> {
  return apiCreateCourse({
    image: '/safe_poster.webp',
    locationId,
  });
}

export async function updateCourseSurveyAssignment(
  courseId: number,
  surveyId: number,
): Promise<void> {
  await removeSurveyAssignment(courseId);
  if (surveyId > 0) {
    await assignSurveyToCourse(courseId, surveyId);
  }
}
